import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import SignInStore from './stores/sign_in_store';
import SERVER_INFO from 'config/server_info';
import { ENDPOINT_MAP } from 'config/endpoints';
import { translate } from 'i18n/localize';
import PreLoginFooter from 'apps/pre_login/components/pre_login_footer';
import PreLoginHeader from 'apps/pre_login/components/pre_login_header';
import HiddenInput from 'apps/pre_login/components/hidden_input';
import Recaptcha from 'apps/pre_login/components/recaptcha';
import oneLoginLogo from 'components/images/onelogin_logo.svg';

const V1_PATH = SERVER_INFO.main_v1_path;
const SIGN_IN_PATH = '/users/sign_in';
const V1_SIGN_IN_URL = `${V1_PATH}${SIGN_IN_PATH}`;
const USER_CENTER_URL = `${V1_PATH}/users/user_center`;
const CONFIRMATION_URL = `${V1_PATH}/users/confirmation/new`;
export const loginAlert = (bootstrap) => bootstrap?.login_error || null;

const ExternalLink = ({children, href, className}) => (
  <a className={className} href={href} target='_blank' rel='noreferrer'>
    {children}
  </a>
)

export const FormInput = ({
  action,
  autoComplete,
  autoFocus = false,
  defaultValue = '',
  id,
  label,
  name,
  type
}) => (
  <p className='signInField'>
    <span className={action ? 'signInLabelRow' : undefined}>
      <label className='signInLabel' htmlFor={id}>{label}</label>
      {action}
    </span>
    <input
      id={id}
      name={name}
      type={type}
      defaultValue={defaultValue}
      autoComplete={autoComplete}
      autoFocus={autoFocus}
    />
  </p>
)

export const FormActions = ({submitDisabled = false}) => (
  <>
    <div className='signInActions'>
      <button
        id='signin'
        className='signInButton signInButtonPrimary'
        type='submit'
        disabled={submitDisabled}
      >
        {translate('login_sign_in')}
      </button>
      <ExternalLink
        className='signInButton signInButtonSecondary signInButtonOneLogin'
        href={ENDPOINT_MAP.LOGIN_ONELOGIN.to}
      >
        <img src={oneLoginLogo} width='60' alt='OneLogin' />
      </ExternalLink>
      <a
        className='signInButton signInButtonSecondary signInButtonUserCenter'
        href={USER_CENTER_URL}
      >
        {translate('login_user_center')}
      </a>
    </div>
    <div className='signInConfirmationLink'>
      <a className='signInLabel' href={CONFIRMATION_URL}>
        {translate('login_confirmation_email')}
      </a>
    </div>
  </>
)

export const SignInForm = ({ bootstrap }) => {
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const handleRecaptchaChange = useCallback((token) => setRecaptchaToken(token), []);
  const recaptchaRequired = Boolean(bootstrap.show_recaptcha);

  const handleSubmit = (event) => {
    const form = event.currentTarget;

    if (window.PasswordCredential) {
      try {
        navigator.credentials.store(new PasswordCredential(form));
      } catch (_e) {} // eslint-disable-line no-empty
    }

    form.action = V1_SIGN_IN_URL
  }

  return (
    <div className='signInForm'>
      <form method='post' action={SIGN_IN_PATH} autoComplete='on' onSubmit={handleSubmit}>
        <HiddenInput name='authenticity_token' value={bootstrap.csrf_token} />
        <HiddenInput name='tz_name' value={Intl.DateTimeFormat().resolvedOptions().timeZone} />
        <HiddenInput name='local' value={navigator.language} />

        <FormInput
          id='user_email'
          name='user[email]'
          type='email'
          label={translate('email_address')}
          defaultValue={bootstrap.email || ''}
          autoComplete='username'
          autoFocus
        />
        <FormInput
          id='user_password'
          name='user[password]'
          type='password'
          label={translate('login_password')}
          autoComplete='current-password'
          action={(
            <a className='signInLabel' href={ENDPOINT_MAP.LOGIN_PASSWORD_RESET.to}>
              {translate('login_forgot_password')}
            </a>
          )}
        />

        {recaptchaRequired && (
          <Recaptcha
            siteKey={bootstrap.recaptcha_site_key}
            onChange={handleRecaptchaChange}
          />
        )}
        <HiddenInput name='g-recaptcha-response' value={recaptchaToken} />

        <FormActions
          submitDisabled={!bootstrap.csrf_token || (recaptchaRequired && !recaptchaToken)}
        />
      </form>
    </div>
  )
}

const SignInApp = observer(() => {
  const [store] = useState(() => SignInStore.create({}));
  const load = useCallback(() => store.loadPage(), [store]);

  useEffect(() => { load(); }, [load]);

  const { data: bootstrap, loading, error } = store;
  const alert = loginAlert(bootstrap);
  const notice = typeof bootstrap?.notice === 'string' ? bootstrap.notice.trim() : '';

  return (
    <div className='signInPage'>
      <div id='centering-container'>
        <div id='flex-container'>
          <PreLoginHeader />
          <div id='content-container'>
            <div id='sign_in_content'>
              <div className='signInTitle'>{translate('login_sign_in')}</div>
              {notice && (
                <p className='signInNotice' role='status'>{notice}</p>
              )}
              {alert && <p className='signInAlert'>{alert}</p>}
              {loading && <p role='status'>{translate('cr_loading')}</p>}
              {error && (
                <>
                  <p className='signInAlert' role='alert'>{translate('login_load_failed')}</p>
                  <button className='signInButton signInButtonPrimary' onClick={load}>
                    {translate('login_try_again')}
                  </button>
                </>
              )}
              {bootstrap && <SignInForm bootstrap={bootstrap} />}
            </div>
          </div>
          <PreLoginFooter />
        </div>
      </div>
    </div>
  )
});

export default SignInApp;
